import { useState } from 'react';
import type { Friendship } from '@callzone/shared';
import { api } from '../lib/api';
import { useStore } from '../store';
import { Avatar } from './Avatar';
import { CloseIcon, PhoneIcon, PullIcon } from './icons';

/**
 * Amigos e conversas privadas. Fica fora das guilds de proposito: amizade e
 * entre pessoas, nao entre membros de um servidor — dois amigos podem nem
 * dividir guild nenhuma.
 */
export function Amigos({ onClose }: { onClose?: () => void }) {
  const amizades = useStore((s) => s.amizades);
  const conversas = useStore((s) => s.conversas);
  const presences = useStore((s) => s.presences);
  const activeDmId = useStore((s) => s.activeDmId);
  const selectDm = useStore((s) => s.selectDm);
  const toast = useStore((s) => s.toast);
  const [busca, setBusca] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const aceitas = amizades.filter((a) => a.status === 'aceita');
  const recebidos = amizades.filter((a) => a.status === 'pendente' && !a.outgoing);
  const enviados = amizades.filter((a) => a.status === 'pendente' && a.outgoing);

  async function guardado(acao: () => Promise<unknown>, sucesso?: string) {
    setOcupado(true);
    try {
      await acao();
      if (sucesso) toast(sucesso);
    } catch (erro) {
      toast(erro instanceof Error ? erro.message : 'Nao consegui fazer isso.', 'error');
    } finally {
      setOcupado(false);
    }
  }

  async function adicionar() {
    const valor = busca.trim();
    if (!valor) return;
    /*
     * Um id e so digito; um apelido aceita letras. Mandar no campo certo evita
     * o servidor procurar um apelido "364709477363712" e nao achar nada.
     */
    const alvo = /^\d+$/.test(valor) ? { userId: valor } : { apelido: valor };
    await guardado(async () => {
      await api.pedirAmizade(alvo);
      setBusca('');
    }, 'Pedido enviado.');
  }

  async function conversar(userId: string) {
    await guardado(async () => {
      const { conversa } = await api.abrirConversa(userId);
      const { conversas: lista } = await api.conversas();
      useStore.getState().setConversas(lista);
      selectDm(conversa.id);
    });
  }

  function Linha({ amizade, children }: { amizade: Friendship; children?: React.ReactNode }) {
    const status = presences[amizade.user.id] ?? 'offline';
    return (
      <div className="amigo-linha">
        <Avatar
          name={amizade.user.nickname}
          color={amizade.user.accentColor}
          size={32}
          presence={status}
        />
        <div className="amigo-quem">
          <div className="nome">{amizade.user.nickname}</div>
          <div className="id" title="Id para adicionar sem errar o apelido">
            {amizade.user.id}
          </div>
        </div>
        <div className="amigo-acoes">{children}</div>
      </div>
    );
  }

  return (
    <div className="amigos">
      <div className="amigos-topo">
        <h2>Amigos</h2>
        {onClose && (
          <button className="icon-btn" onClick={onClose} title="Fechar" aria-label="Fechar">
            <CloseIcon />
          </button>
        )}
      </div>

      <div className="amigos-add">
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void adicionar()}
          placeholder="Apelido ou id da pessoa"
          maxLength={40}
        />
        <button className="btn small" disabled={ocupado || !busca.trim()} onClick={() => void adicionar()}>
          Adicionar
        </button>
      </div>

      <div className="amigos-lista">
        {recebidos.length > 0 && (
          <>
            <div className="member-group-title">Pedidos recebidos — {recebidos.length}</div>
            {recebidos.map((a) => (
              <Linha key={a.user.id} amizade={a}>
                <button
                  className="btn small"
                  disabled={ocupado}
                  onClick={() => void guardado(() => api.aceitarAmizade(a.user.id), 'Agora voces sao amigos.')}
                >
                  Aceitar
                </button>
                <button
                  className="btn ghost small"
                  disabled={ocupado}
                  onClick={() => void guardado(() => api.removerAmizade(a.user.id), 'Pedido recusado.')}
                >
                  Recusar
                </button>
              </Linha>
            ))}
          </>
        )}

        {enviados.length > 0 && (
          <>
            <div className="member-group-title">Enviados — {enviados.length}</div>
            {enviados.map((a) => (
              <Linha key={a.user.id} amizade={a}>
                <span className="amigo-aguardando">aguardando</span>
                <button
                  className="btn ghost small"
                  disabled={ocupado}
                  onClick={() => void guardado(() => api.removerAmizade(a.user.id), 'Pedido cancelado.')}
                >
                  Cancelar
                </button>
              </Linha>
            ))}
          </>
        )}

        <div className="member-group-title">Amigos — {aceitas.length}</div>
        {aceitas.length === 0 && (
          <div className="amigos-vazio">
            Ninguem ainda. Adicione pelo apelido ou peça o id da pessoa.
          </div>
        )}
        {aceitas.map((a) => {
          const conversa = conversas.find((c) => c.user.id === a.user.id);
          return (
            <Linha key={a.user.id} amizade={a}>
              <button
                className={`btn ghost small ${conversa && conversa.id === activeDmId ? 'ativo' : ''}`}
                disabled={ocupado}
                onClick={() => void conversar(a.user.id)}
                title="Conversar no privado"
              >
                <PullIcon style={{ width: 14, height: 14 }} />
                Conversar
              </button>
              <button
                className="btn ghost small"
                disabled={ocupado}
                title="Desfazer amizade"
                onClick={() => {
                  if (!window.confirm(`Desfazer a amizade com ${a.user.nickname}?`)) return;
                  void guardado(() => api.removerAmizade(a.user.id), 'Amizade desfeita.');
                }}
              >
                Remover
              </button>
            </Linha>
          );
        })}

        {conversas.length > 0 && (
          <>
            <div className="member-group-title" style={{ marginTop: 18 }}>
              Conversas
            </div>
            {conversas.map((c) => (
              <button
                key={c.id}
                className={`amigo-linha conversa ${c.id === activeDmId ? 'ativa' : ''}`}
                onClick={() => selectDm(c.id)}
              >
                <Avatar
                  name={c.user.nickname}
                  color={c.user.accentColor}
                  size={30}
                  presence={presences[c.user.id] ?? 'offline'}
                />
                <div className="amigo-quem">
                  <div className="nome">{c.user.nickname}</div>
                </div>
                <PhoneIcon style={{ width: 15, height: 15, opacity: 0.5 }} />
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
